/**
 * New Conversation Modal
 * Allows starting a new SMS/iMessage conversation with a lead
 * Can also create a new lead if none exists
 */

import React, { useState, useEffect, useRef } from 'react';
import { Lead } from '../../types';
import { useCRM } from '../../utils/CRMContext';
import { formatPhoneNumber, validatePhoneNumber, sendSMS } from '../../utils/sendblue';
import { useAuth } from '../../utils/AuthContext';
import { 
  X, 
  Search, 
  User, 
  Phone, 
  Send,
  AlertCircle,
  Check,
  MessageSquare,
  UserPlus,
  ArrowLeft
} from 'lucide-react';
import './NewConversationModal.css';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationStarted?: (leadId: string) => void;
}

type ModalView = 'search' | 'create' | 'compose';

const NewConversationModal: React.FC<NewConversationModalProps> = ({
  isOpen,
  onClose,
  onConversationStarted
}) => {
  const { leads, createLead } = useCRM();
  const { user } = useAuth();
  
  const [view, setView] = useState<ModalView>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // New lead form state
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  // Check if a phone number is valid for messaging
  const isValidPhone = (phone: string | number | undefined | null): boolean => {
    if (!phone) return false;
    try {
      const phoneStr = String(phone);
      const formatted = formatPhoneNumber(phoneStr);
      return validatePhoneNumber(formatted);
    } catch {
      return false;
    }
  };

  // Filter leads by search query
  const filteredLeads = leads.filter(lead => {
    // Must have a phone number
    if (!lead.phone) return false;
    
    // If no search query, show all leads with phone numbers
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.toLowerCase();
    // Ensure phone is converted to string before calling replace
    const phoneStr = String(lead.phone || '');
    const phone = phoneStr.replace(/\D/g, '');
    const email = String(lead.email || '').toLowerCase();
    const queryDigits = query.replace(/\D/g, '');
    
    return (
      fullName.includes(query) ||
      email.includes(query) ||
      (queryDigits && phone.includes(queryDigits))
    );
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setView('search');
      setSearchQuery('');
      setSelectedLead(null);
      setMessage('');
      setError(null);
      setSuccess(false);
      setNewFirstName('');
      setNewLastName('');
      setNewPhone('');
      setNewEmail('');
      setCreating(false);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Focus appropriate input when view changes
  useEffect(() => {
    if (view === 'search') {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else if (view === 'create') {
      setTimeout(() => firstNameInputRef.current?.focus(), 100);
    } else if (view === 'compose') {
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  }, [view]);

  // Format phone for display
  const formatPhoneDisplay = (phone: string | number | undefined | null): string => {
    if (phone === undefined || phone === null) return 'No phone';
    const phoneStr = String(phone);
    const cleaned = phoneStr.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phoneStr;
  };

  // Get initials
  const getInitials = (firstName?: string, lastName?: string): string => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  // Handle lead selection
  const handleSelectLead = (lead: Lead) => {
    if (!isValidPhone(lead.phone)) {
      setError('This lead has an invalid phone number');
      return;
    }
    setSelectedLead(lead);
    setError(null);
    setView('compose');
  };

  // Handle creating a new lead
  const handleCreateLead = async () => {
    if (!newFirstName.trim() || !newLastName.trim() || !newPhone.trim()) {
      setError('First name, last name, and phone are required');
      return;
    }

    // Validate phone number
    if (!isValidPhone(newPhone)) {
      setError('Please enter a valid phone number (e.g., 555-123-4567)');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const formattedPhone = formatPhoneNumber(newPhone);
      
      const leadId = await createLead({
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        phone: formattedPhone,
        email: newEmail.trim() || '',
        status: 'new',
        source: 'unspecified',
        budget: 0,
        projectType: '',
        timeline: '',
        location: '',
        notes: 'Created from Messages for new conversation',
      });

      // Find the newly created lead
      // Give it a moment to appear in the leads array
      setTimeout(() => {
        const newLead = leads.find(l => l.id === leadId);
        if (newLead) {
          setSelectedLead(newLead);
        } else {
          // Create a temporary lead object if not found yet
          setSelectedLead({
            id: leadId,
            firstName: newFirstName.trim(),
            lastName: newLastName.trim(),
            phone: formattedPhone,
            email: newEmail.trim() || '',
            status: 'new',
            source: 'unspecified',
            budget: 0,
            projectType: '',
            timeline: '',
            location: '',
            notes: '',
            createdAt: new Date(),
            updatedAt: new Date(),
          });
        }
        setView('compose');
      }, 500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lead');
      setCreating(false);
    }
  };

  // Handle sending message
  const handleSend = async () => {
    if (!selectedLead || !message.trim() || !selectedLead.phone || sending) return;

    setSending(true);
    setError(null);

    try {
      const result = await sendSMS(
        selectedLead.phone,
        message.trim(),
        selectedLead.id,
        user?.id,
        user?.name,
        selectedLead.firstName,
        selectedLead.lastName
      );

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          if (selectedLead.id) {
            onConversationStarted?.(selectedLead.id);
          }
          onClose();
        }, 1000);
      } else {
        setError(result.error || 'Failed to send message');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (view === 'compose') {
        setView('search');
        setSelectedLead(null);
      } else if (view === 'create') {
        setView('search');
      } else {
        onClose();
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (view === 'compose' && selectedLead && message.trim()) {
        handleSend();
      } else if (view === 'create' && newFirstName && newLastName && newPhone) {
        handleCreateLead();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="new-conversation-overlay" onClick={onClose}>
      <div 
        className="new-conversation-modal" 
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="ncm-header">
          <h2>
            <MessageSquare size={20} />
            {view === 'create' ? 'New Contact' : 'New Conversation'}
          </h2>
          <button className="ncm-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="ncm-content">
          {view === 'search' && (
            // Lead Search View
            <>
              <div className="ncm-search">
                <Search size={16} className="ncm-search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="ncm-leads-list">
                {/* Create New Lead Option */}
                <div
                  className="ncm-lead-item ncm-create-new"
                  onClick={() => setView('create')}
                >
                  <div className="ncm-lead-avatar create">
                    <UserPlus size={18} />
                  </div>
                  <div className="ncm-lead-info">
                    <span className="ncm-lead-name">Create New Contact</span>
                    <span className="ncm-lead-phone">Add a new person to start messaging</span>
                  </div>
                </div>

                {filteredLeads.length === 0 && searchQuery.trim() ? (
                  <div className="ncm-empty">
                    <User size={32} />
                    <p>No leads match "{searchQuery}"</p>
                    <button 
                      className="ncm-create-btn"
                      onClick={() => setView('create')}
                    >
                      <UserPlus size={16} />
                      Create New Contact
                    </button>
                  </div>
                ) : (
                  filteredLeads.slice(0, 50).map(lead => (
                    <div
                      key={lead.id}
                      className={`ncm-lead-item ${!isValidPhone(lead.phone) ? 'invalid-phone' : ''}`}
                      onClick={() => handleSelectLead(lead)}
                    >
                      <div className="ncm-lead-avatar">
                        {getInitials(lead.firstName, lead.lastName)}
                      </div>
                      <div className="ncm-lead-info">
                        <span className="ncm-lead-name">
                          {lead.firstName} {lead.lastName}
                        </span>
                        <span className="ncm-lead-phone">
                          <Phone size={12} />
                          {lead.phone ? formatPhoneDisplay(lead.phone) : 'No phone'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                {filteredLeads.length > 50 && (
                  <div className="ncm-more-results">
                    +{filteredLeads.length - 50} more leads. Refine your search.
                  </div>
                )}
              </div>

              {error && (
                <div className="ncm-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </>
          )}

          {view === 'create' && (
            // Create New Lead View
            <>
              <div className="ncm-create-form">
                <button 
                  className="ncm-back"
                  onClick={() => {
                    setView('search');
                    setError(null);
                  }}
                >
                  <ArrowLeft size={16} />
                  Back to search
                </button>

                <p className="ncm-create-info">
                  Enter contact details to create a new lead and start messaging.
                </p>

                <div className="ncm-form-row">
                  <div className="ncm-form-field">
                    <label>First Name *</label>
                    <input
                      ref={firstNameInputRef}
                      type="text"
                      value={newFirstName}
                      onChange={e => setNewFirstName(e.target.value)}
                      placeholder="John"
                      disabled={creating}
                    />
                  </div>
                  <div className="ncm-form-field">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={newLastName}
                      onChange={e => setNewLastName(e.target.value)}
                      placeholder="Smith"
                      disabled={creating}
                    />
                  </div>
                </div>

                <div className="ncm-form-field">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={e => setNewPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    disabled={creating}
                  />
                </div>

                <div className="ncm-form-field">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder="john@example.com"
                    disabled={creating}
                  />
                </div>

                {error && (
                  <div className="ncm-error">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="ncm-form-actions">
                  <span className="ncm-hint">⌘+Enter to create</span>
                  <button
                    className="ncm-send-btn"
                    onClick={handleCreateLead}
                    disabled={!newFirstName.trim() || !newLastName.trim() || !newPhone.trim() || creating}
                  >
                    {creating ? 'Creating...' : (
                      <>
                        <UserPlus size={16} />
                        Create & Message
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {view === 'compose' && selectedLead && (
            // Message Compose View
            <>
              <div className="ncm-selected-lead">
                <button 
                  className="ncm-back"
                  onClick={() => {
                    setView('search');
                    setSelectedLead(null);
                    setError(null);
                  }}
                >
                  <ArrowLeft size={16} />
                  Change recipient
                </button>
                <div className="ncm-recipient">
                  <div className="ncm-lead-avatar large">
                    {getInitials(selectedLead.firstName, selectedLead.lastName)}
                  </div>
                  <div className="ncm-lead-info">
                    <span className="ncm-lead-name">
                      {selectedLead.firstName} {selectedLead.lastName}
                    </span>
                    <span className="ncm-lead-phone">
                      <Phone size={12} />
                      {formatPhoneDisplay(selectedLead.phone)}
                    </span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="ncm-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {success && (
                <div className="ncm-success">
                  <Check size={16} />
                  Message sent! Opening conversation...
                </div>
              )}

              <div className="ncm-compose">
                <textarea
                  ref={messageInputRef}
                  placeholder="Type your message..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  disabled={sending || success}
                  rows={4}
                />
                <div className="ncm-compose-footer">
                  <span className="ncm-hint">⌘+Enter to send</span>
                  <button
                    className="ncm-send-btn"
                    onClick={handleSend}
                    disabled={!message.trim() || sending || success}
                  >
                    {sending ? (
                      'Sending...'
                    ) : success ? (
                      <>
                        <Check size={16} />
                        Sent
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;
